import { ErrorBoundary } from 'components/Errors/ErrorBoundary';
import IconButton from 'components/IconButton';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import { ModuleGuard } from 'components/ModuleGuard';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { Fragment, Suspense, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Collapse,
  Button,
} from 'reactstrap';
import { currentChartTypeAtom } from 'views/state';

import CAList from './CAList';
import { CAType } from './config';
import CurrentCharts from './currentCharts';
import HistoryCharts from './historyCharts';
import {
  severityFiltersAtom,
  timeSpanAtom,
  intervalAtom,
  caTypeAtom,
  showHistoryChartCountsAtom,
  historyChartsOpenAtom,
  currentVulnChartsOpenAtom,
  isObsoleteGroupingVulnListingOpenAtom,
  isGroupingByQidVulnListingOpenAtom,
  hostCountsAtom,
  appliedCAIdsAtom,
} from './state';
import VulnerabilityMetrics from './VulnMetrics';
import AppliedHostInfo from '../components/AppliedHostInfo';
import HistoryChartHeader from '../components/HistoryChart/HistoryChartHeader';
import '../styles.css';
import SeverityFilterDropdown from '../components/SeverityFilterDropdown';
import {
  ChartType,
  DashboardType,
  ModalDialogData,
  TooltipLabelsType,
} from '../types';

const CADashboard = () => {
  const [caType, setCAType] = useAtom(caTypeAtom);
  const hostCounts = useAtomValue(hostCountsAtom);
  const [currentChartType, setCurrentChartType] = useAtom(currentChartTypeAtom);
  const setIsObsoleteGroupingVulnListingOpen = useSetAtom(
    isObsoleteGroupingVulnListingOpenAtom,
  );
  const setIsGroupingByQidVulnListingOpen = useSetAtom(
    isGroupingByQidVulnListingOpenAtom,
  );

  // TOGGLE THE COLLAPSE FOR HISTORY CHARTS AND CURRENT CHARTS SECTIONS
  const [historyChartsOpen, setHistoryChartsOpen] = useAtom(
    historyChartsOpenAtom,
  );
  const [currentVulnChartsOpen, setCurrentVulnChartsOpen] = useAtom(
    currentVulnChartsOpenAtom,
  );

  const [modalComponentData, setModalComponentData] =
    useState<ModalDialogData>();

  const listingScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      if (window.location.href.includes(`#${DashboardType.CA}`)) {
        if (listingScrollRef.current) {
          listingScrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, 1000);

    return () => clearTimeout(scrollTimeout);
  }, [DashboardType.CA]);

  function getToolTipLabels() {
    const toolTipLabels: TooltipLabelsType = [];
    if (caType === CAType.Server || caType === CAType.All) {
      const serverCounts = hostCounts[CAType.Server];
      toolTipLabels.push({
        label: 'Servers',
        hostsCount: serverCounts,
      });
    }

    if (caType === CAType.Workstation || caType === CAType.All) {
      const workstationCounts = hostCounts[CAType.Workstation];
      toolTipLabels.push({
        label: CAType.Workstation,
        hostsCount: workstationCounts,
      });
    }
    return toolTipLabels;
  }

  return (
    <Fragment>
      <div className="container-fluid p-0">
        <nav className="navbar navbar-expand-sm bg-white border-top-0 border border-bottom-light sticky-top px-0">
          <div
            className="row w-100 align-items-center"
            style={{ margin: '0px' }}
          >
            {/* First Column: Left-aligned content */}
            <Col xs="12" md="3" lg="6">
              <AppliedHostInfo appliedHostIdsAtom={appliedCAIdsAtom} />
            </Col>

            {/* Second Column: Right-aligned content */}
            <Col xs="12" md="9" lg="6" className="d-flex justify-content-end">
              <div className="d-flex flex-row">
                <SeverityFilterDropdown
                  severityFiltersAtom={severityFiltersAtom}
                />
                <IconButton
                  id="serverButton"
                  iconClass="fa fa-server fa-lg"
                  tooltip={
                    caType === CAType.Server ? 'Show All' : 'Servers Only'
                  }
                  active={caType === CAType.Server}
                  onClick={() => {
                    setCAType(
                      caType === CAType.Server ? CAType.All : CAType.Server,
                    );
                  }}
                />
                <IconButton
                  id="workstation"
                  iconClass="fa fa-desktop fa-lg"
                  tooltip={
                    caType === CAType.Workstation
                      ? 'Show All'
                      : 'Workstations Only'
                  }
                  active={caType === CAType.Workstation}
                  onClick={() => {
                    setCAType(
                      caType === CAType.Workstation
                        ? CAType.All
                        : CAType.Workstation,
                    );
                  }}
                />
              </div>
            </Col>
          </div>
        </nav>
        <Card>
          <CardHeader>
            <Row>
              <Col className="col-auto">
                <CardTitle tag="h4">
                  Vulnerabilities Trending History&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() => setHistoryChartsOpen(!historyChartsOpen)}
                  >
                    <i
                      className={
                        historyChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={historyChartsOpen}>
                  <div className="d-inline float-right">
                    <HistoryChartHeader
                      intervalAtom={intervalAtom}
                      showHistoryChartCountsAtom={showHistoryChartCountsAtom}
                      timeSpanAtom={timeSpanAtom}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={historyChartsOpen}>
            <CardBody>
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                  <HistoryCharts
                    tooltipLabels={getToolTipLabels()}
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </ErrorBoundary>
            </CardBody>
          </Collapse>
        </Card>
        <Card>
          <CardHeader>
            <Row>
              <Col>
                <CardTitle tag="h4">
                  Current Vulnerabilities&nbsp;
                  <Button
                    outline={true}
                    color="primary"
                    size="sm"
                    onClick={() =>
                      setCurrentVulnChartsOpen(!currentVulnChartsOpen)
                    }
                  >
                    <i
                      className={
                        currentVulnChartsOpen
                          ? 'bi bi-chevron-up'
                          : 'bi bi-chevron-down'
                      }
                    ></i>
                  </Button>
                </CardTitle>
              </Col>
              <Col>
                <Collapse isOpen={currentVulnChartsOpen}>
                  <div className="d-inline float-right">
                    <IconButton
                      id="chartTypeButton"
                      iconClass={
                        currentChartType === ChartType.Bar
                          ? 'fa fa-chart-pie fa-lg'
                          : 'fa fa-chart-bar fa-lg'
                      }
                      tooltip={
                        currentChartType === ChartType.Bar
                          ? 'Display Pie Charts'
                          : 'Display Bar Charts'
                      }
                      onClick={() => {
                        setCurrentChartType(
                          currentChartType === ChartType.Bar
                            ? ChartType.Doughnut
                            : ChartType.Bar,
                        );
                      }}
                    />
                  </div>
                </Collapse>
              </Col>
            </Row>
          </CardHeader>
          <Collapse isOpen={currentVulnChartsOpen}>
            <CardBody>
              <div className="row">
                <Suspense fallback={<LoadingScreen />}>
                  <CurrentCharts
                    setModalComponentData={setModalComponentData}
                  />
                </Suspense>
              </div>
            </CardBody>
          </Collapse>
        </Card>
        <VulnerabilityMetrics />
      </div>
      <div ref={listingScrollRef} aria-hidden></div>
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <CAList setModalComponentData={setModalComponentData} />
        </Suspense>
      </ErrorBoundary>
      <ModalDialog
        title={modalComponentData?.title}
        subTitle={modalComponentData?.subTitle}
        childComponent={modalComponentData?.bodyComponent}
        onClose={() => {
          setModalComponentData(undefined);
          setIsObsoleteGroupingVulnListingOpen(false);
          setIsGroupingByQidVulnListingOpen(false);
        }}
      />
    </Fragment>
  );
};

export default CADashboard;
